import { z } from 'zod';
import { artifactClaimEvidenceSchema, artifactReviewVerdictSchema, } from './artifact-claim-evidence.schema.js';
import { structuredCitationSchema } from './citation-validation.schemas.js';
import { specCurrentBehaviorEntrySchema, specRemainderRequirementEntrySchema, specDisagreementQuestionSchema, } from './spec-remainder-contract.js';
export const memberConfidenceLevelSchema = z.enum(['high', 'medium', 'low']);
export const specAgentAssumptionSchema = z
    .object({
    assumption_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    statement: z.string().min(1).max(8192),
    confidence: memberConfidenceLevelSchema,
    evidence_handles: z.array(z.string().max(512)).optional(),
    section_anchor: z.string().max(512).nullable().optional(),
})
    .strict();
export const confidenceMapByAnchorSchema = z.record(memberConfidenceLevelSchema);
export const specAgentDraftMetadataSchema = z
    .object({
    schema_version: z.literal(1),
    assumptions: z.array(specAgentAssumptionSchema).optional(),
    section_confidence: confidenceMapByAnchorSchema.optional(),
    overall_review_confidence: memberConfidenceLevelSchema.nullable().optional(),
    source_dispatch_attempt_id: z.string().uuid().nullable().optional(),
    agent_profile_key: z.string().max(128).nullable().optional(),
    /** DC-366 — explicit provisional draft path (owner/admin). */
    provisional_draft_from_clarification: z.boolean().optional(),
    /** DC-366 / DC-447 — correlation to clarification episode when applicable. */
    clarification_episode_id: z.string().uuid().nullable().optional(),
    artifact_claim_evidence: artifactClaimEvidenceSchema.optional(),
    artifact_review: artifactReviewVerdictSchema.optional(),
    /** Story 2 — cited current-state baseline. Absent on pre-remainder specs. */
    current_behavior: z.array(specCurrentBehaviorEntrySchema).max(256).optional(),
    remainder_requirements: z.array(specRemainderRequirementEntrySchema).max(256).optional(),
    disagreement_questions: z.array(specDisagreementQuestionSchema).max(6).optional(),
    /**
     * SHA-256 of the normalized ticket description this draft was generated from
     * (`computeIssueDescriptionDigest`). Server-written at persist, never model-supplied.
     * Intake compares an incoming webhook's description against it to answer "was this
     * spec generated from this description?" — the anchor that stops metadata-only
     * ticket edits from regenerating the spec. Null when the ticket had no description.
     */
    source_description_digest: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable()
        .optional(),
})
    .strict();
/**
 * Approve is refused while a version is an already-stamped provisional draft, or while it
 * still carries ticket-versus-repository questions. A later draft that drops those questions
 * can be approved.
 */
export function specificationVersionClarificationApprovalBlock(metadata) {
    const parsed = specAgentDraftMetadataSchema.safeParse(metadata);
    if (!parsed.success)
        return null;
    if (parsed.data.provisional_draft_from_clarification) {
        return 'CLARIFICATION_PROVISIONAL_DRAFT';
    }
    if ((parsed.data.disagreement_questions?.length ?? 0) > 0) {
        return 'DISAGREEMENT_QUESTIONS_UNRESOLVED';
    }
    return null;
}
/**
 * Assistant-visible structured output for the spec-generation AgentRuntime profile (DC-365).
 * Persisted to `domain.work_item_specification_versions` after validation.
 */
export const specAgentGenerationAssistantPayloadSchema = z
    .object({
    body_markdown: z.string().min(1).max(512_000),
    citations_json: z.array(structuredCitationSchema).max(256).nullable().optional(),
    /**
     * Server-written fields are omitted from the model-facing contract: the prompt
     * embeds this schema verbatim (see the DC-449 drift test), and asking the model for
     * a provenance digest would invite it to invent one. The persist path stamps
     * `source_description_digest` itself and always wins.
     */
    spec_agent_metadata: specAgentDraftMetadataSchema
        .omit({ source_description_digest: true })
        .nullable()
        .optional(),
})
    .strict();
//# sourceMappingURL=spec-agent-draft-metadata.schema.js.map